import React from 'react';
import { css } from '@emotion/react';
import { Checkbox, Chip, Grid, ListItemText, MenuItem, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { Separator } from '@onward-delivery/design/separator';

import { genName as genSubregionName } from '@/components/Subregions/utils';
import CeilingsSection from './CeilingsSection';

import { equipmentKeys, toEquipmentMap } from '../../utils';

import { SERVICE_TYPES, VEHICLE_TYPES, EQUIPMENT_TYPES } from '../../constants';
import { SectionHeader, SectionSubheader } from './blocks';
import SelectableChip from './SelectableChip';
import ScheduleSection from './ScheduleSection';

// `Separator` is a bare rule, so the spacing a `Divider` would carry lives here.
const BETWEEN_SECTIONS_RULE = css`
    margin: 24px 0 20px;
`;

// The dialog's only tab. Split out of index.jsx, which had grown past 1,200 lines — this is display
// only, so every value and setter arrives as a prop.
const SetupTab = ({
    updated,
    setUpdates,
    setupErrors,
    ceilingErrors,
    schedule,
    setSchedule,
    scheduleError,
    isNew,
    todayKey,
    regions,
    setRegions,
    subregions,
    subregionsOrdered,
    shippingPartners,
    setShippingPartners,
    shipping_partners,
}) => (
    <>
        <SectionHeader>Resource</SectionHeader>
        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <TextField
                size="small"
                fullWidth
                variant="outlined"
                required
                label="Name"
                value={updated?.name || ''}
                error={!!setupErrors?.name}
                helperText={setupErrors?.name || ''}
                onChange={(e) => {
                    setUpdates((prev) => ({ ...prev, name: e.target.value }));
                }}
            />
        </Grid>
        <ScheduleSection
            schedule={schedule}
            setSchedule={setSchedule}
            scheduleError={scheduleError}
            isNew={isNew}
            todayKey={todayKey}
            updated={updated}
            setUpdates={setUpdates}
            setupErrors={setupErrors}
        />

        <SectionHeader>Vehicle</SectionHeader>

        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <Grid
                item
                css={css`
                    flex: 1;
                    margin-right: 20px;
                `}
            >
                <TextField
                    size="small"
                    select
                    fullWidth
                    variant="outlined"
                    label="Vehicle type"
                    value={updated?.vehicle_type ?? ''}
                    onChange={(e) => {
                        const value = e.target.value === '' ? null : e.target.value;
                        setUpdates((prev) => ({ ...prev, vehicle_type: value }));
                    }}
                >
                    <MenuItem value="">Not specified</MenuItem>
                    {VEHICLE_TYPES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>
                            {label}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
            <Grid
                item
                css={css`
                    flex: 1;
                `}
            >
                <TextField
                    size="small"
                    fullWidth
                    type="number"
                    variant="outlined"
                    label="Box size (ft)"
                    value={updated?.box_size ?? ''}
                    inputProps={{ min: 0 }}
                    error={setupErrors?.boxSize}
                    helperText={setupErrors?.boxSize ? 'Must be 0 or greater' : ''}
                    onChange={(e) => {
                        const value = e.target.value === '' ? null : Number(e.target.value);
                        setUpdates((prev) => ({ ...prev, box_size: value }));
                    }}
                />
            </Grid>
        </Grid>

        {/* One vocabulary with orders' recommended_equipment / equipment_overrides, so what a
            resource carries can be compared against what an order needs. Chips rather than the
            toggle cards this replaced: fifteen keys is too many for two-across cards, and only
            two of them had icons. */}
        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <Grid
                item
                css={css`
                    flex: 1;
                `}
            >
                <SectionHeader>Equipment</SectionHeader>
                <SectionSubheader>Tap to set what this resource carries.</SectionSubheader>
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: wrap;
                        gap: 8px;
                    `}
                >
                    {EQUIPMENT_TYPES.map(({ value, label }) => {
                        const selected = equipmentKeys(updated?.equipment).includes(value);

                        return (
                            <SelectableChip
                                key={value}
                                label={label}
                                selected={selected}
                                onClick={() => {
                                    const current = equipmentKeys(updated?.equipment);
                                    const next = selected
                                        ? current.filter((key) => key !== value)
                                        : [...current, value];
                                    setUpdates((prev) => ({ ...prev, equipment: toEquipmentMap(next) }));
                                }}
                            />
                        );
                    })}
                </Grid>
            </Grid>
        </Grid>

        {/* Postgres text[] — send an array back. Empty means no restriction. */}
        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <Grid
                item
                css={css`
                    flex: 1;
                `}
            >
                <SectionHeader>Service Levels</SectionHeader>
                <SectionSubheader>Tap to set which levels this resource can service.</SectionSubheader>
                <Grid
                    container
                    direction="row"
                    css={css`
                        flex-wrap: wrap;
                        gap: 8px;
                    `}
                >
                    {SERVICE_TYPES.map(({ value, label }) => {
                        const selectedTypes = updated?.service_types || [];
                        const selected = selectedTypes.includes(value);

                        return (
                            <SelectableChip
                                key={value}
                                label={label}
                                selected={selected}
                                onClick={() => {
                                    const next = selected
                                        ? selectedTypes.filter((type) => type !== value)
                                        : [...selectedTypes, value];
                                    setUpdates((prev) => ({ ...prev, service_types: next }));
                                }}
                            />
                        );
                    })}
                </Grid>
            </Grid>
        </Grid>

        {/* Ceilings used to be a second tab. One surface now, so the operator sets a resource up
            and caps it in one pass rather than discovering the cap behind a tab. Sits with Vehicle,
            which is what a ceiling is a property of — Regions is about where it goes. */}
        <Separator css={BETWEEN_SECTIONS_RULE} />

        <CeilingsSection updated={updated} setUpdates={setUpdates} ceilingErrors={ceilingErrors} />

        <Separator css={BETWEEN_SECTIONS_RULE} />

        <SectionHeader>Regions &amp; Partner</SectionHeader>

        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <TextField
                size="small"
                select
                fullWidth
                variant="outlined"
                label="Shipping Partner"
                value={shippingPartners || []}
                SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                        return selected
                            .map((id) => {
                                const sp = shipping_partners.find((partner) => partner.shipper_id === id);
                                return sp ? sp.shipper.business_name : '--';
                            })
                            .join(', ');
                    },
                }}
                onChange={(e) => {
                    setShippingPartners(e.target.value);
                }}
                css={css`
                    background-color: white;
                    width: 100%;
                    padding-right: 2px;
                    margin-top: 0.2rem;
                `}
            >
                {shipping_partners.map((partner) => {
                    return (
                        <MenuItem key={partner.shipper_id} value={partner.shipper_id}>
                            <Checkbox color="primary" checked={shippingPartners.includes(partner.shipper_id)} />
                            <ListItemText primary={partner.shipper.business_name} />
                        </MenuItem>
                    );
                })}
            </TextField>
        </Grid>
        <Grid
            direction="row"
            container
            css={css`
                margin-bottom: 20px;
                flex: 0;
                flex-basis: 0;
            `}
        >
            <Autocomplete
                multiple
                fullWidth
                disableClearable
                size="small"
                options={subregionsOrdered}
                getOptionLabel={(option) => {
                    const idx = subregionsOrdered.findIndex(
                        (resource) => resource.subregion_id === option.subregion_id
                    );

                    return genSubregionName(option, idx);
                }}
                getOptionDisabled={(option) => regions.map(([id]) => id).includes(option.subregion_id)}
                value={regions || []}
                onChange={(e, value, reason) => {
                    switch (reason) {
                        case 'select-option': {
                            const added = value.slice(-1)[0];
                            const idx = subregionsOrdered.findIndex(
                                (resource) => resource.subregion_id === added.subregion_id
                            );

                            setRegions((prev) => {
                                return [...prev, [added.subregion_id, idx]];
                            });
                            break;
                        }
                    }
                }}
                renderTags={() => null}
                renderInput={(params) => {
                    return (
                        <TextField
                            {...params}
                            size="small"
                            variant="outlined"
                            label="Subregions"
                            placeholder="Select a subregion"
                            fullWidth
                        />
                    );
                }}
            />
        </Grid>
        {/* Selected zones as removable chips, not stacked rows. */}
        <Grid
            direction="row"
            container
            css={css`
                flex: 0;
                flex-basis: 0;
                flex-wrap: wrap;
                gap: 8px;
                max-height: 96px;
                overflow-y: auto;
            `}
        >
            {regions.map(([id, ordering]) => (
                <Chip
                    key={id}
                    label={genSubregionName(subregions?.[id] || {}, ordering)}
                    onDelete={() => {
                        setRegions((prev) => prev.filter(([old]) => old !== id));
                    }}
                    css={css`
                        border-radius: 16px;
                        font-size: 12px;
                        height: 28px;
                        background-color: #f0f0f0;
                        color: #4c4c4c;
                    `}
                />
            ))}
        </Grid>
    </>
);

export default SetupTab;
